import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAppState } from '@/contexts/AppStateContext';

interface SignUpUserData {
  firstName: string;
  lastName: string;
  jobTitle?: string;
}

/**
 * Compatibility hook that wraps AppStateContext for auth operations.
 * The actual auth state is managed centrally in AppStateContext.
 */
export const useAuth = () => {
  const { 
    user, 
    session, 
    isInitializing: loading,
    signOut: contextSignOut,
  } = useAppState();

  const signUp = useCallback(async (email: string, password: string, userData: SignUpUserData) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/success`,
        data: {
          first_name: userData.firstName,
          last_name: userData.lastName,
          job_title: userData.jobTitle || null,
          business_name: '',
        },
      },
    });
    return { error };
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  }, []);

  const signInWithGoogle = useCallback(async () => {
    console.log('[useAuth] 🚀 signInWithGoogle called');
    console.log('[useAuth] 📍 Redirect URL:', `${window.location.origin}/choose-plan`);
    
    // PRODUCTION CONFIG: Final OAuth settings for SaaS with mandatory Google Business scope
    // - access_type: 'offline' = get refresh_token for persistent access
    // - prompt: 'select_account consent' = force account selection + consent screen
    const { error, data } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/choose-plan`,
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
        scopes: 'openid email profile https://www.googleapis.com/auth/business.manage',
      },
    });
    
    console.log('[useAuth] 📤 OAuth result:', { 
      hasError: !!error, 
      errorMessage: error?.message,
      redirectUrl: data?.url 
    });
    
    return { error, data };
  }, []);

  const signOut = useCallback(async () => {
    await contextSignOut();
  }, [contextSignOut]);

  return {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
  };
};

// Keep AuthProvider as a no-op for compatibility
// The actual provider is now AppStateProvider
export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};
